import React, { useContext, useRef, useState } from 'react';

import { type Island, type Panel, type PanelItem, Vertex2 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { RenderPanel } from './render-panel';
import { useDraggableSvg } from '@r-services/ui-interactions';

/**
 * Panel card styling constants — shared with RenderCustomPanelsSideView for layout calculations
 */
export const PANEL_CARD_WIDTH = 100;
export const TITLE_AREA_HEIGHT = 6;
export const LEGEND_LINE_HEIGHT = 2.5;
export const LEGEND_PADDING = 3;

const LABEL_FONT_SIZE_BASE = 2.5;
const ITEM_LABEL_FONT_SIZE_BASE = DIMENSION_FONT_SIZE;

/**
 * SKU label colors for custom items (same as main SKU layer)
 */
const SKU_LABEL_COLORS = {
	customAppliance: '#ADD8E6', // Light Blue
	cutout: '#ADD8E6',          // Light Blue
	customCutout: '#90EE90',    // Green
} as const;

/**
 * Gets the label color for a custom item
 */
const getItemLabelColor = (panelItem: PanelItem): string => {
	const { sku, label } = panelItem.item;

	if (sku === 'CUTOUT') {
		// Custom cutout (utility) shows green
		if (label && label.toUpperCase().includes('UTILITY')) {
			return SKU_LABEL_COLORS.customCutout;
		}
		return SKU_LABEL_COLORS.cutout;
	}

	return SKU_LABEL_COLORS.customAppliance;
};

/**
 * Gets the display label for a custom item
 */
const getItemLabel = (panelItem: PanelItem): string => {
	const { customLabel, label, sku } = panelItem.item;

	if (customLabel) return customLabel;
	if (label) return label;
	return sku;
};

/**
 * Props for the draggable panel card component
 */
export type DraggableCustomPanelCardProps = {
	panel: Panel;
	island: Island;
	customItems: Array<PanelItem>;
	cardX: number;
	cardY: number;
	cardHeight: number;
	legendHeight: number;
	scaleFactor: number;
	scaledContentHeight: number;
};

/**
 * DraggableCustomPanelCard Component
 * Individual panel card that can be dragged using the ui-interactions system.
 */
export const DraggableCustomPanelCard = (props: DraggableCustomPanelCardProps): React.ReactElement => {
	const {
		panel,
		island,
		customItems,
		cardX,
		cardY,
		cardHeight,
		legendHeight,
		scaleFactor,
		scaledContentHeight
	} = props;

	const { controller } = useContext(AppContext);
	const fontSizeFactor = controller?.options?.dimensionFontSizeFactor ?? 1;
	const itemLabelFontSize = ITEM_LABEL_FONT_SIZE_BASE * fontSizeFactor;
	const legendLineHeight = LEGEND_LINE_HEIGHT * fontSizeFactor;

	const movableRef = useRef<SVGGElement>(null);
	const [position, setPosition] = useState({ x: cardX, y: cardY });
	const panelCenterX = position.x + PANEL_CARD_WIDTH / 2;
	const panelCenterY = position.y + TITLE_AREA_HEIGHT + scaledContentHeight / 2 + (panel.size.height * 0.2 * scaleFactor);

	useDraggableSvg({
		ref: movableRef,
		enabled: [true, true],
		cursor: ['grab', 'grabbing'],
		position,
		bBox: {
			origin: new Vertex2({ dataIn: { x: 0, y: 0 } }),
			size: { width: 1000, height: 1000, depth: 0 },
			padding: [0, 0, 0, 0]
		},
		onDragging: ({ position: { new: p } }) => {
			setPosition(p);
			return true;
		}
	});

	return (
		<g
			id={`custom-panel-card-${panel.id}`}
			ref={movableRef}
			style={{ pointerEvents: 'auto' }}
		>
			{/* Card background */}
			<rect
				x={position.x}
				y={position.y}
				width={PANEL_CARD_WIDTH}
				height={cardHeight}
				fill="white"
				stroke="#333333"
				strokeWidth={0.1}
				rx={1}
				ry={1}
			/>

			{/* Clip the panel rendering to the card */}
			<defs>
				<clipPath id={`custom-panel-clip-${panel.id}`}>
					<rect x={position.x} y={position.y} width={PANEL_CARD_WIDTH} height={cardHeight} />
				</clipPath>
			</defs>

			{/* Scale panel around its center point to fit in card */}
			<g clipPath={`url(#custom-panel-clip-${panel.id})`}>
				<g transform={`translate(${panelCenterX}, ${panelCenterY}) scale(${scaleFactor}) translate(${-panelCenterX}, ${-panelCenterY})`}>
					<RenderPanel
						islandId={island.id}
						panelId={panel.id}
						offsetX={panelCenterX}
						offsetY={panelCenterY}
						showDimensions={false}
						showApps={true}
						showSkuLayer={false}
					/>
				</g>
			</g>

			{/* Title background */}
			<rect
				x={position.x}
				y={position.y}
				width={PANEL_CARD_WIDTH}
				height={TITLE_AREA_HEIGHT}
				fill="white"
				rx={1}
				ry={1}
			/>
			{/* Card title */}
			<text
				x={position.x + PANEL_CARD_WIDTH / 2}
				y={position.y + TITLE_AREA_HEIGHT / 2}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={LABEL_FONT_SIZE_BASE}
				fontFamily="Arial, sans-serif"
				fontWeight="bold"
				fill="#333333"
			>
				{island.description} - {panel.label}
			</text>

			{/* Legend list (rendered after panel so it's on top) */}
			<g transform={`translate(${position.x + 2}, ${position.y + cardHeight - legendHeight + LEGEND_PADDING})`}>
				{customItems.map((item, itemIndex) => {
					const itemY = itemIndex * legendLineHeight;
					const labelColor = getItemLabelColor(item);

					return (
						<g key={item.id} transform={`translate(0, ${itemY})`}>
							{/* Color indicator */}
							<rect
								x={0}
								y={0}
								width={2}
								height={1.5}
								fill={labelColor}
								stroke="#333333"
								strokeWidth={0.03}
								rx={0.2}
								ry={0.2}
							/>
							{/* Item label */}
							<text
								x={2.5}
								y={0.75}
								dominantBaseline="central"
								fontSize={itemLabelFontSize}
								fontFamily="Arial, sans-serif"
								fill="#333333"
							>
								{getItemLabel(item)}
							</text>
						</g>
					);
				})}
			</g>
		</g>
	);
};
